"use client";

import { useRef } from "react";
import Image from "next/image";
import { GroqImage } from "@/helpers/Image";

import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import { PortableText } from "@portabletext/react";
import { customRichTextComponent } from "@/sanity/sanityPortableText";

import BlackPill from "@/components/getting-started/BlackPill";
import { CTAButton } from "@/shadcn/ui/button";

import { PageMentoringSchema } from "@/sanity/pages/PageMentoringTemplate";

import assetImg from "@/public/mentoring/who.png";
import gridBGimg from "@/public/lp2/dear/grid.svg";
import blurImg from "@/public/lp2/dear/blur.svg";
import CheckMarkGreenIcon from "@/public/mentoring/check-mark-green.svg";

if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger, useGSAP);
}

export default function WhoSection({ data }: { data: PageMentoringSchema }) {
  const containerRef = useRef<HTMLDivElement | any>(undefined);
  const content1Ref = useRef<HTMLDivElement | any>(undefined);
  const content2Ref = useRef<HTMLDivElement | any>(undefined);

  useGSAP(
    () => {
      const whoTL = gsap.timeline({
        defaults: { opacity: 0, ease: "power2.inOut", duration: 0.25 },
      });

      whoTL
        .from(content1Ref.current.children, {
          duration: 1,
          stagger: 0.2,
          delay: 0.5,
          scrollTrigger: {
            trigger: content1Ref.current,
            end: "center center",
            scrub: 1,
          },
        })
        .from(content2Ref.current.children, {
          y: "2rem",
          stagger: 0.1,
          delay: 0.5,
          scrollTrigger: {
            trigger: content2Ref.current,
            end: "center center",
            scrub: 1,
          },
        });
    },
    { scope: containerRef }
  );

  return (
    <section
      ref={containerRef}
      className="w-full relative z-[1] overflow-x-clip lg:pt-[10rem] pb-[20rem] lg:pb-[30rem]"
    >
      <div className="container-lp mx-auto relative max-w-[188rem]">
        <div className="flex flex-col lg:flex-row justify-center items-center gap-[4rem] mb-[-20rem]">
          <div
            ref={content1Ref}
            className="flex flex-col justify-center items-start gap-[3rem] lg:basis-[50%] order-2 lg:order-1 lg:pe-[12rem] relative"
          >
            {/* grid */}
            <div className="w-full absolute left-[-50%] top-0 z-[-1]">
              <Image src={gridBGimg} alt="grid background" className="w-full" />
            </div>
            {/* shadow/blur */}
            <div className="w-full absolute left-[-50%] top-[-20rem] z-[-1]">
              <Image
                src={blurImg}
                alt="blur background"
                className="dear-section-blur w-full"
              />
            </div>
            {/*  */}
            {data?.ambitiousTitles && (
              <PortableText
                value={data?.ambitiousTitles ?? []}
                components={customRichTextComponent({
                  block: {
                    h6: ({ children }) => (
                      <BlackPill
                        text={children as string}
                        className="w-fit !text-[1.6rem] lg:!text-[1.8rem] !leading-[110%]"
                      />
                    ),
                    h3: ({ children }) => (
                      <h3 className="gradient-title text-[5rem] lg:text-[8rem] font-[500] tracking-[-0.35rem] lg:tracking-[-0.29rem] leading-[110%]">
                        {children}
                      </h3>
                    ),
                  },
                  listItem: {
                    bullet: ({ children }) => (
                      <li className="text-white text-[2.25rem] font-[400] leading-[110%] w-full flex items-center gap-4 mb-[2rem]">
                        <Image
                          className="w-[3rem] h-[3rem] min-w-[3rem] min-h-[3rem]"
                          src={CheckMarkGreenIcon}
                          alt="check"
                        />
                        <span>{children}</span>
                      </li>
                    ),
                  },
                })}
              />
            )}

            {data?.ambitiousCTA && <CTAButton data={data?.ambitiousCTA} />}
          </div>

          <div
            ref={content2Ref}
            className="lg:basis-[50%] order-1 lg:order-2 flex justify-center items-center relative"
          >
            <GroqImage
              src={assetImg}
              alt="Asset image"
              className="w-full lg:max-w-max"
            />
            {/* blurs */}
            <div className="absolute z-[1] w-full h-full bg-[#1ECC97] opacity-20 left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 rounded-full blur-[10.25rem]"></div>
            <div className="absolute z-[0] w-1/2 h-1/2 bg-[#1ECC97] opacity-20 right-[-30%] bottom-[-30%] rounded-full blur-[10.25rem]"></div>
            {/* end blurs */}
          </div>
        </div>
      </div>
    </section>
  );
}
